import { Expose, Exclude, Type } from 'class-transformer';
import { DocumentSnapshot } from 'firebase/firestore';
import { _FirebaseDocument } from './Models';

/**
 * In-force terms-of-use pointer (Firestore: system/public_config.terms),
 * projected server-side from config/terms (MYAC-824). Drives the re-consent
 * gate: a user must have accepted a version whose sequence is
 * >= minimum_accepted_sequence. Ordering is by sequence — the version string
 * is an opaque key, never parsed.
 */
export class TermsPointer {
    @Expose()
    public current_version?: string;

    @Expose()
    public current_sequence?: number;

    @Expose()
    public minimum_accepted_sequence?: number;
}

/**
 * Contact/help modal content (Firestore: system/public_config.help_contact).
 * Every field is optional — the modal renders each element only when present,
 * so ops can turn any of them off simply by clearing the value in Firestore.
 */
export class HelpContactConfig {
    @Expose()
    public show_photo?: boolean;

    @Expose()
    public photo_url?: string;

    @Expose()
    public name?: string;

    @Expose()
    public role?: string;

    @Expose()
    public phone?: string;

    @Expose()
    public email?: string;

    @Expose()
    public support_url?: string;
}

/** Plain label ids for the three chat entry buttons. Workspace data: they differ
 *  between Plain workspaces, which is why they live here and not in code. */
export class SupportChatLabelTypeIds {
    @Expose()
    public question?: string;

    @Expose()
    public bug?: string;

    @Expose()
    public improvement?: string;
}

/**
 * MYAC-1348 — the Plain support chat (Firestore: system/public_config.support_chat).
 * Absent app_id = the chat is off in this environment and the help button keeps
 * opening the contact modal, so ops can enable or roll it back without a deploy.
 */
export class SupportChatConfig {
    @Expose()
    public app_id?: string;

    @Expose()
    @Type(() => SupportChatLabelTypeIds)
    public label_type_ids?: SupportChatLabelTypeIds;
}

@Exclude()
export class PublicConfig extends _FirebaseDocument {
    @Expose()
    public contact_sales_url?: string;

    @Expose()
    public self_serve_enabled?: boolean;

    @Expose()
    @Type(() => HelpContactConfig)
    public help_contact?: HelpContactConfig;

    @Expose()
    @Type(() => SupportChatConfig)
    public support_chat?: SupportChatConfig;

    /** Hotfix 2026-08-31 (MYAC-1158): the assessment scheduler is not
     *  reinstated on the new engine — default OFF hides the QMS/TD scheduling
     *  cards until this flips. */
    @Expose()
    public enable_assessment_scheduling?: boolean;

    @Expose()
    public enable_legacy_plans?: boolean;

    @Expose()
    public enable_td_applied_standards?: boolean;

    @Expose()
    public enable_td_device_features?: boolean;

    @Expose()
    public enable_qms_suppliers_customers?: boolean;

    @Expose()
    public enable_qms_nc_capa?: boolean;

    /** MYAC-1228 — the smart rerun card beside the Pilotage chat. Default OFF;
     *  per-tenant override via Tenant.feature_flags.enable_smart_rerun. */
    @Expose()
    public enable_smart_rerun?: boolean;

    /** Release 2026-09-03 — the Playbook page (MYAC-1051). Default OFF;
     *  per-tenant override via Tenant.feature_flags.enable_playbook. */
    @Expose()
    public enable_playbook?: boolean;

    @Expose()
    public enable_task_directives?: boolean;

    @Expose()
    public enable_tracex_export?: boolean;

    @Expose()
    @Type(() => TermsPointer)
    public terms?: TermsPointer;

    // MYAC-773 dual-run switch: when true, producers write to the per-type
    // command queues (datasource_sync_{type}, folder_discover, assessment_run)
    // instead of flipping the legacy trigger_* fields. Default off (legacy path)
    // so it is a zero-redeploy, instantly-reversible rollout.
    @Expose()
    public enable_command_queue_producers?: boolean;

    /** MYAC-1237 — the risk-analysis surface (nav entry + views). Default OFF;
     *  per-tenant override via Tenant.feature_flags.enable_risk_analysis. */
    @Expose()
    public enable_risk_analysis?: boolean;

    /** MYAC-1515 — company connections: the Connections page in the company
     *  settings and "use a connection of your company" when creating a
     *  datasource. Default OFF; per-tenant override via
     *  Tenant.feature_flags.enable_tenant_connections. */
    @Expose()
    public enable_tenant_connections?: boolean;

    /** MYAC-918 — your conversations: the handle's list, the column on the
     *  conversation page, the title heading, rename and the personal read state.
     *  Default OFF; per-tenant override via Tenant.feature_flags.enable_conversations. */
    @Expose()
    public enable_conversations?: boolean;

    collection(): string {
        return "system";
    }

    static fromFirebase(o: DocumentSnapshot): PublicConfig | null {
        return _FirebaseDocument._fromFirebase(PublicConfig, o);
    }
}

